import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaTachometerAlt, 
  FaChartBar, 
  FaFileAlt, 
  FaLink, 
  FaHeadset, 
  FaUsers, 
  FaRocket,
  FaArrowRight,
  FaClock,
  FaShieldAlt,
  FaMobile,
  FaDesktop,
  FaCog,
  FaCheckCircle,
  FaBell,
  FaDownload
} from 'react-icons/fa';
import EmailVerificationWarning from '../components/EmailVerificationWarning';
import { getUser } from '../utils/tokenUtils';

const DashboardPage = () => {
  const [showEmailWarning, setShowEmailWarning] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const currentUser = getUser();
    setUser(currentUser);
  }, []);

  // Load Tawk.to script
  useEffect(() => {
    const loadTawkTo = () => {
      if (window.Tawk_API) return; // Already loaded
      
      window.Tawk_API = window.Tawk_API || {};
      window.Tawk_LoadStart = new Date();
      
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://embed.tawk.to/5b5a0878e21878736ba2580b/1j7m6856s';
      script.charset = 'UTF-8';
      script.setAttribute('crossorigin', '*');
      
      const firstScript = document.getElementsByTagName('script')[0];
      firstScript.parentNode.insertBefore(script, firstScript);
    };

    loadTawkTo();
  }, []);
  const features = [
    {
      icon: <FaFileAlt className="h-8 w-8" />,
      title: "Document Management",
      description: "Create, organize, and manage all your legal documents from one centralized dashboard."
    },
    {
      icon: <FaLink className="h-8 w-8" />,
      title: "URL Management",
      description: "Track and manage all your short links, view analytics, and monitor click performance."
    },
    {
      icon: <FaHeadset className="h-8 w-8" />,
      title: "Support Overview",
      description: "Monitor customer support tickets, response times, and team performance metrics."
    },
  ];

  const benefits = [
    {
      title: "Centralized Control",
      description: "Manage all your business tools and data from one powerful dashboard",
      icon: <FaTachometerAlt className="h-6 w-6" />
    },
    {
      title: "Time Savings",
      description: "Reduce time spent switching between tools with our unified interface",
      icon: <FaClock className="h-6 w-6" />
    },
    {
      title: "Business Growth",
      description: "Scale your business efficiently with integrated tools and automation",
      icon: <FaRocket className="h-6 w-6" />
    }
  ];

  const stats = [
    { number: "100%", label: "Unified Platform" },
    { number: "50%", label: "Time Saved" },
    { number: "24/7", label: "Access" },
    { number: "99.9%", label: "Uptime" }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-purple-600 via-purple-700 to-purple-800 text-white">
        <div className="absolute inset-0 bg-black opacity-5"></div>
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-72 h-72 bg-white opacity-5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute top-1/4 right-0 w-96 h-96 bg-white opacity-5 rounded-full translate-x-1/2 -translate-y-1/2"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 bg-white bg-opacity-10 backdrop-blur-sm rounded-full text-sm font-medium mb-6 animate-fadeInUp">
              <FaTachometerAlt className="mr-2" />
              Unified Business Dashboard
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fadeInUp leading-tight">
              Dashboard
              <span className="block text-3xl md:text-5xl mt-3 font-light opacity-90">Complete Business Management</span>
            </h1>
            
            <p className="text-xl md:text-2xl mb-10 max-w-4xl mx-auto opacity-90 animate-fadeInUp leading-relaxed">
              Manage your entire business from one powerful dashboard. Track performance, manage documents, 
              monitor support tickets, and analyze your growth with comprehensive analytics and insights.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center animate-fadeInUp">
              <Link
                to="/register"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold bg-white text-purple-700 border border-gray-200 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transform transition-all duration-200 hover:bg-purple-50 hover:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-200"
              >
                <FaRocket className="mr-3 text-purple-700 transition-colors duration-200" />
                <span className="">Get Started Free</span>
              </Link>
              
              <Link
                to="/about"
                className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-semibold border-2 border-white text-white rounded-xl hover:bg-white hover:text-purple-600 transform hover:-translate-y-1 transition-all duration-300 backdrop-blur-sm"
              >
                Learn More
                <div className="absolute inset-0 bg-white rounded-xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
              </Link>
            </div>
            
            {/* Stats */}
            <div className="flex flex-wrap justify-center gap-8 mt-16 animate-fadeInUp">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl font-bold">{stat.number}</div>
                  <div className="text-sm opacity-75">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-br from-purple-700 via-purple-800 to-purple-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 drop-shadow">
              Everything You Need in One Place
            </h2>
            <p className="text-xl text-purple-100 max-w-2xl mx-auto">
              Access all your business tools, data, and insights from a single, powerful dashboard
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group relative bg-white/5 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-purple-900/30 overflow-hidden animate-fadeInUp"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="relative p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-800 rounded-2xl mb-6 shadow-lg">
                    <div className="text-purple-200 text-2xl">
                      {feature.icon}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3 drop-shadow">{feature.title}</h3>
                  <p className="text-purple-100 leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Dashboard?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Transform how you manage your business with our comprehensive dashboard solution
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="bg-gray-50 rounded-2xl p-8 text-center hover:bg-purple-50 hover:text-purple-600 transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="text-purple-600 mb-4 flex justify-center">
                  {benefit.icon}
                </div>
                <h3 className="text-lg font-semibold mb-3">{benefit.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gradient-to-br from-purple-700 via-purple-800 to-purple-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 drop-shadow">
              How It Works
            </h2>
            <p className="text-xl text-purple-100 max-w-2xl mx-auto">
              Get started with your unified business dashboard in three simple steps
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-purple-600 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <span className="text-white font-bold text-2xl">1</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Create Account</h3>
              <p className="text-purple-200">Sign up and set up your business profile and preferences</p>
            </div>
            
            <div className="text-center">
              <div className="bg-purple-600 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <span className="text-white font-bold text-2xl">2</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Access Dashboard</h3>
              <p className="text-purple-200">Log in to your personalized dashboard with all your tools</p>
            </div>
            
            <div className="text-center">
              <div className="bg-purple-600 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <span className="text-white font-bold text-2xl">3</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Manage & Grow</h3>
              <p className="text-purple-200">Use integrated tools to manage your business and track growth</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-purple-700 via-purple-800 to-purple-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Centralize Your Business?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
            Join businesses that have streamlined their operations with our unified dashboard
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="btn btn-lg bg-white text-purple-600 hover:bg-gray-100 font-semibold px-8 py-4 text-lg"
            >
              Get Started Free
              <FaArrowRight className="ml-2" />
            </Link>
            <Link
              to="/about"
              className="btn btn-lg border-2 border-white text-white hover:bg-white hover:text-purple-600 font-semibold px-8 py-4 text-lg"
            >
              Learn More
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DashboardPage;
